/* eslint-disable react-native/no-inline-styles */
/* eslint-disable prettier/prettier */
import React, { useMemo, useRef, useState } from 'react';
import { Text, TouchableWithoutFeedback, View } from 'react-native';
import { useHover } from 'react-native-web-hooks';
import i18n from 'i18n-js';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import ColorWrapper from '@styles/colors';
import { isWeb } from '@helpers';
import { SmallLoading } from '@components/loading';

const Element = (props) => {
  const {
    C,
    onPress,
    text,
    subText,
    style,
    selected,
    preselected,
    icon,
    loading,
    scale = initialScale,
    font = initialFont,
    hitSlop,
    disabled = false,
    styleContainerOverride = {},
    styleBoxOverride = {},
    textStyleOverride = {},
    testID,
  } = props;
  const ref = useRef(null);
  const hovered = useHover(ref);
  const [pressed, setPressed] = useState(false);

  const name = (() => {
    if (selected) {
      return 'selected';
    } else {
      if (pressed) {
        return 'pressed';
      }
      if (hovered) {
        return 'hovered';
      }
      if (preselected) {
        return 'preselected';
      }
      return 'unselected';
    }
  })();

  const st = style[name];

  const box = {};
  Object.keys(st.box).forEach((i) => {
    box[i] = scale(st.box[i], false);
  });

  return (
    <View testID={testID} style={[isWeb ? { cursor: 'pointer' } : {}, styleContainerOverride]}>
      <TouchableWithoutFeedback
        activeOpacity={1}
        onPress={onPress}
        ref={ref}
        onPressIn={() => {
          setPressed(true);
        }}
        onPressOut={() => {
          setPressed(false);
        }}
        hitSlop={hitSlop || { top: 15, bottom: 15, left: 15, right: 15 }}
        disabled={disabled}
      >
        <View
          style={[
            isWeb && {outline: 'none'},
            box,
            st.border,
            {
              backgroundColor: C(st.bg),
              flexDirection: 'row',
              alignItems: 'center',
              borderColor: C(st.border?.borderColor),
            },
            styleBoxOverride,
          ]}
        >
          {loading && (
            <View style={{ width: icon?.width || scale(14), height: icon?.height || scale(12), marginRight: scale(5) }}>
              <SmallLoading />
            </View>
          )}
          {!loading && icon && icon.name && (
            <View style={{ marginRight: scale(5) }}>
              <Svg
                icon={icon.name}
                color={st.color || st.colorTop}
                width={icon.width || scale(14)}
                height={icon.height || scale(12)}
                key={`${selected}_${text}`}
                style={icon.style || {}}
              />
            </View>
          )}
          <Text style={[font[st.text.top ? st.text.top : st.text], { color: C(st.color || st.colorTop) }, textStyleOverride]}>{i18n.t(text)}</Text>
          {subText && st.colorBottom && st.text.bottom && <Text style={[font[st.text.bottom], { color: C(st.colorBottom) }]}>{i18n.t(subText)}</Text>}
        </View>
      </TouchableWithoutFeedback>
    </View>
  );
};

const Selector = (props) => {
  const { variant, C } = props;

  const st = useMemo(() => {
    switch (variant) {
      case 'WEB': {
        const box = {
          paddingLeft: 12,
          paddingRight: 12,
          paddingBottom: 4,
          paddingTop: 4,
          borderRadius: 18,
        };
        const text = 'Subhead 1';
        return {
          selected: {
            bg: '',
            color: 'Label/Light/Unselected Web Hover',
            box,
            text,
          },
          pressed: {
            bg: '',
            color: 'Label/Light/Unselected Web',
            box,
            text,
          },
          hovered: {
            bg: 'Background/Light/hover menuWeb',
            color: 'Label/Light/Unselected Web Hover',
            box,
            text,
          },
          unselected: {
            bg: false,
            color: 'Label/Light/Unselected Web',
            box,
            text,
          },
        };
      }
      case 'L': {
        const box = {
          paddingLeft: 14,
          paddingRight: 14,
          paddingBottom: 4.5,
          paddingTop: 4,
          borderRadius: 18.5,
          height: 29,
        };
        const text = 'Subhead 1';
        return {
          preselected: {
            bg: 'Button/Light/Secondary',
            color: 'Label/Light/Secondary',
            box,
            text,
          },
          selected: {
            bg: 'Selected/Light/Primary',
            color: 'Button/Light/Primary Text',
            box,
            text,
          },
          pressed: {
            bg: 'Button/Light/Only Text pressed',
            color: 'Label/Light/Primary',
            box,
            text,
          },
          hovered: {
            bg: 'Button/Light/Only Text hover',
            color: 'Label/Light/Primary',
            box,
            text,
          },
          unselected: {
            bg: false,
            color: 'Label/Light/Unselected',
            box,
            text,
          },
        };
      }
      case 'M': {
        const box = {
          paddingLeft: 12,
          paddingRight: 12,
          paddingBottom: 3.5,
          paddingTop: 3.5,
          borderRadius: 18,
        };
        const text = 'Subhead 1';
        return {
          selected: {
            bg: 'Selected/Light/Primary',
            color: 'Button/Light/Primary Text',
            box,
            text,
          },
          pressed: {
            bg: 'Button/Light/Only Text pressed',
            color: 'Label/Light/Primary',
            box,
            text,
          },
          hovered: {
            bg: 'Button/Light/Only Text hover',
            color: 'Label/Light/Primary',
            box,
            text,
          },
          unselected: {
            bg: false,
            color: 'Label/Light/Unselected',
            box,
            text,
          },
        };
      }
      case 'LIST': {
        const box = {
          paddingLeft: 10,
          paddingRight: 10,
          paddingBottom: 8,
          paddingTop: 9,
          borderRadius: 10,
          minWidth: 125,
        };
        const text = {
          top: 'Button 1',
          bottom: 'Subhead 1',
        };

        return {
          selected: {
            bg: 'Selected/Light/List item',
            colorTop: 'Button/Light/Primary Text',
            colorBottom: 'Label/Light/AlwaysLight Secondary',
            box,
            text,
          },
          pressed: {
            bg: 'Button/Light/Only Text pressed',
            colorTop: 'Label/Light/Primary',
            colorBottom: 'Label/Light/Tertiary',
            box,
            text,
          },
          hovered: {
            bg: 'Button/Light/Only Text hover',
            colorTop: 'Label/Light/Primary',
            colorBottom: 'Label/Light/Tertiary',
            box,
            text,
          },
          unselected: {
            bg: false,
            colorTop: 'Label/Light/Primary',
            colorBottom: 'Label/Light/Tertiary',
            box,
            text,
          },
        };
      }
      case 'SIDE LIST': {
        const box = {
          paddingLeft: 9,
          paddingRight: 9,
          paddingBottom: 11,
          paddingTop: 11,
          borderRadius: 10,
          minWidth: 125,
        };
        const text = {
          top: 'Body Large',
          bottom: 'Subhead 1',
        };

        return {
          selected: {
            bg: 'Selected/Light/List item',
            colorTop: 'Button/Light/Primary Text',
            colorBottom: 'Label/Light/AlwaysLight Secondary',
            box,
            text,
          },
          pressed: {
            bg: 'Button/Light/Only Text pressed',
            colorTop: 'Label/Light/Primary',
            colorBottom: 'Label/Light/Tertiary',
            box,
            text,
          },
          hovered: {
            bg: 'Button/Light/Only Text hover',
            colorTop: 'Label/Light/Primary',
            colorBottom: 'Label/Light/Tertiary',
            box,
            text,
          },
          unselected: {
            bg: false,
            colorTop: 'Label/Light/Primary',
            colorBottom: 'Label/Light/Tertiary',
            box,
            text,
          },
        };
      }
      case 'OUTLINE': {
        const box = {
          paddingLeft: 6,
          paddingRight: 12,
          paddingBottom: 1,
          paddingTop: 2,
          borderRadius: 18,
        };
        const border = {
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: `Hairline/${C.scheme === 'light' ? 'Light' : 'Dark'}/No trans [mobile]`,
        };
        const text = 'Subhead 2';
        return {
          selected: {
            bg: 'Selected/Light/Secondary',
            color: 'Label/Light/Primary',
            box,
            text,
            border: { ...border, borderStyle: 'solid' },
          },
          pressed: {
            bg: 'Button/Light/Secondary pressed',
            color: 'Label/Light/Unselected',
            box,
            text,
            border,
          },
          hovered: {
            bg: 'Button/Light/Secondary hover',
            color: 'Label/Light/Unselected',
            box,
            text,
            border,
          },
          unselected: {
            bg: false,
            color: 'Label/Light/Unselected',
            box,
            text,
            border,
          },
        };
      }
    }
  }, [variant, C]);

  return <Element {...props} C={C} style={st} />;
};

export default ColorWrapper(Selector);
